import {
    Component,
    HostListener,
    Injector,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    atlas,
    LOCAL_CLAIMS,
    LOCAL_USER_STATE,
    LOGOUT,
    SETTINGS,
    SETTINGS_MOBILE_MENU
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    ActionSheet,
    Color,
    forLifeOf,
    getNavigationItemForNode,
    LifecycleAware,
    NavigationItem,
    NavigationState,
    ActionSheetService,
    LsqAppRootComponent,
    LsqNavigationComponent,
    SessionService,
    UnsupportedBrowserModal
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

@LifecycleAware()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AppComponent extends LsqAppRootComponent implements OnInit, OnDestroy {
    @ViewChild('mobileNavTemplate') public mobileNavTemplateRef: TemplateRef<any>;
    @ViewChild(LsqNavigationComponent) public lsqNavigationComponent: LsqNavigationComponent;

    public navigationState = new NavigationState();
    public showFooter = false;
    public hideNavigationItems = false;

    public isLoggedIn$: Observable<boolean>;
    public logoutNavigationItem = getNavigationItemForNode(atlas, LOGOUT, {
        queryParams: { immediate: true }
    });
    public settingsNavigationItem = getNavigationItemForNode(atlas, SETTINGS);

    private _hostUrls = [
        `/${UiRoutes.Login}`,
        `/${UiRoutes.Health}`,
        `/${UiRoutes.NotFound}`,
        `/${UiRoutes.NotSupported}`,
        `/${UiRoutes.Logout}`,
        '/_healthz',
        '/eula'
    ];

    public isOnHostRoute$ = this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) =>
            this._hostUrls.some(
                (hostUrl) =>
                    event.urlAfterRedirects?.startsWith(hostUrl) || event.url.startsWith(hostUrl)
            )
        )
    );

    constructor(
        private _i18n: I18n,
        private _userService: UserService,
        private _sessionService: SessionService,
        private _actionSheetService: ActionSheetService,
        public injector: Injector,
        public viewContainerRef: ViewContainerRef,
        public router: Router
    ) {
        super(injector, viewContainerRef, router);
    }

    @HostListener('window:beforeunload')
    public async beforePageUnload(): Promise<void> {
        await this._sessionService.pageClosed();
    }

    public ngOnInit(): void {
        super.ngOnInit();
        this._initNavigation();

        // This prevents the store from being stored over multiple sessions.
        this._userService.logout$.pipe(forLifeOf(this)).subscribe(() => {
            this.navigationState.user = null;
            this.navigationState.companies = [];
        });

        // Update navigationState and notifications when the user changes.
        this._userService.user$
            .pipe(
                filter((user) => !!user && !!user.currentCompany),
                forLifeOf(this)
            )
            .subscribe(async (user) => {
                this.navigationState.user = user;
                this.navigationState.companies = user.companies;
                this.idleService.reset();
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                first()
            )
            .subscribe(() => {
                // We allow the user to use partially supported browsers, but show a disclaimer modal.
                if (this.detectBrowserService.isPartiallySupportedBrowser()) {
                    this.modalService.showModal(new UnsupportedBrowserModal());
                }
            });

        this.isLoggedIn$ = this._userService.user$.pipe(map((user) => !!user));
    }

    public ngOnDestroy(): void {}

    public openMobileNav(): void {
        this._actionSheetService.showActionSheet(
            new ActionSheet({
                templateRef: this.mobileNavTemplateRef,
                closeButtonColor: Color.White,
                stickToTop: true
            })
        );
    }

    private _initNavigation(): void {
        this._buildSettingsDropdownNavigationItems();
        this._buildAllNavigationItems();
    }

    private _buildAllNavigationItems(): void {
        this.navigationState.allNavigationItems = [
            ...this.navigationState.navigationItems,
            ...this.navigationState.settingsDropdownNavigationItems
        ];
    }

    private _buildSettingsDropdownNavigationItems(): void {
        const settingsNavigationItems: NavigationItem[] = [];
        settingsNavigationItems.push(getNavigationItemForNode(atlas, LOCAL_CLAIMS));
        settingsNavigationItems.push(getNavigationItemForNode(atlas, LOCAL_USER_STATE));
        settingsNavigationItems.push(
            getNavigationItemForNode(atlas, SETTINGS_MOBILE_MENU, {
                hideInDesktop: true
            })
        );

        settingsNavigationItems.push({
            ...this.logoutNavigationItem,
            hideInDesktop: true
        });

        this.navigationState.settingsDropdownNavigationItems = settingsNavigationItems;
    }
}
